import React, { useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import "./BasicModal.css";

/**
 * BasicModal
 * - theme.css buton sınıflarıyla uyumlu (btn-md, btn-md-primary, btn-md-ghost)
 * - Escape ile kapatma
 * - Backdrop tıklayınca kapatma (opsiyonel)
 * - Body scroll lock
 * - Basit focus trap (Tab ile modal dışına kaçmaz)
 * -   Tek <img> yakalarsa otomatik zoom/pan/pinch
 *
 * Props:
 * - open: boolean
 * - title: string | ReactNode
 * - children: ReactNode
 * - onClose: () => void
 * - actions: ReactNode (footer actions)
 * - size: "sm" | "md" | "lg" | "xl" (default "lg")
 * - closeOnBackdrop: boolean (default true)
 * - showClose: boolean (default true)
 * - ariaLabel: string (title yoksa)
 */
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function extractSingleImg(nodeChildren) {
  const arr = React.Children.toArray(nodeChildren).filter(Boolean);
  if (arr.length !== 1) return null;

  const only = arr[0];
  if (!React.isValidElement(only)) return null;

  // direkt <img />
  if (typeof only.type === "string" && only.type.toLowerCase() === "img") {
    const src = only.props?.src;
    if (!src) return null;
    return {
      src,
      alt: only.props?.alt,
      className: only.props?.className,
    };
  }

  // wrapper'ları aç: Fragment / div / span
  const isFragment = only.type === React.Fragment;
  const isSimpleWrapper =
    typeof only.type === "string" && ["div", "span"].includes(only.type.toLowerCase());

  if (isFragment || isSimpleWrapper) {
    return extractSingleImg(only.props?.children);
  }

  return null;
}

function ZoomableImage({ src, alt, className }) {
  const stageRef = useRef(null);

  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  // drag state
  const dragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    baseTx: 0,
    baseTy: 0,
  });

  // pinch state
  const pointersRef = useRef(new Map());
  const pinchRef = useRef({
    active: false,
    startDist: 0,
    startScale: 1,
    startTx: 0,
    startTy: 0,
    startCenterX: 0,
    startCenterY: 0,
  });

  // src değişince reset (modal içinde farklı resimler açıldığında)
  useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
    dragRef.current.active = false;
    pinchRef.current.active = false;
    pointersRef.current.clear();
  }, [src]);

  const applyZoomAtPoint = (nextScale, pointX, pointY) => {
    const s = scale || 1;
    const ns = clamp(nextScale, 1, 6);
    if (ns === s) return;

    const r = ns / s;
    // t' = (1-r)*p + r*t
    const nextTx = (1 - r) * pointX + r * tx;
    const nextTy = (1 - r) * pointY + r * ty;

    setScale(ns);
    setTx(nextTx);
    setTy(nextTy);
  };

  const zoomBy = (delta, clientX, clientY) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = clamp(clientX - rect.left, 0, rect.width);
    const py = clamp(clientY - rect.top, 0, rect.height);

    const nextScale = scale * (1 + delta);
    applyZoomAtPoint(nextScale, px, py);
  };

  const reset = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  const onWheel = (e) => {
    // trackpad/mouse wheel zoom
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.12 : -0.12;
    zoomBy(delta, e.clientX, e.clientY);
  };

  const onPointerDown = (e) => {
    const stage = stageRef.current;
    if (!stage) return;

    stage.setPointerCapture?.(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    const rect = stage.getBoundingClientRect();

    // pinch başlat (2 parmak)
    if (pointersRef.current.size === 2) {
      const pts = Array.from(pointersRef.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy);

      const cx = (pts[0].x + pts[1].x) / 2 - rect.left;
      const cy = (pts[0].y + pts[1].y) / 2 - rect.top;

      pinchRef.current = {
        active: true,
        startDist: dist || 1,
        startScale: scale || 1,
        startTx: tx,
        startTy: ty,
        startCenterX: cx,
        startCenterY: cy,
      };

      dragRef.current.active = false;
      return;
    }

    // drag başlat (tek parmak/mouse)
    if (pointersRef.current.size === 1) {
      dragRef.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        baseTx: tx,
        baseTy: ty,
      };
    }
  };

  const onPointerMove = (e) => {
    if (!stageRef.current) return;

    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    const stage = stageRef.current;
    const rect = stage.getBoundingClientRect();

    // pinch update
    if (pinchRef.current.active && pointersRef.current.size === 2) {
      const pts = Array.from(pointersRef.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy) || 1;

      const curCenterX = (pts[0].x + pts[1].x) / 2 - rect.left;
      const curCenterY = (pts[0].y + pts[1].y) / 2 - rect.top;

      const start = pinchRef.current;
      const rawNextScale = start.startScale * (dist / start.startDist);
      const ns = clamp(rawNextScale, 1, 6);
      const r = ns / (start.startScale || 1);

      // ölçekleme: mevcut merkez etrafında
      const scaledTx = (1 - r) * curCenterX + r * start.startTx;
      const scaledTy = (1 - r) * curCenterY + r * start.startTy;

      // pinch sırasında merkez kaydıysa (iki parmakla sürükleme gibi) onu da ekle
      const dcx = curCenterX - start.startCenterX;
      const dcy = curCenterY - start.startCenterY;

      setScale(ns);
      setTx(scaledTx + dcx);
      setTy(scaledTy + dcy);
      return;
    }

    // drag update
    if (dragRef.current.active && pointersRef.current.size === 1) {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setTx(dragRef.current.baseTx + dx);
      setTy(dragRef.current.baseTy + dy);
    }
  };

  const onPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);

    // pinch bitti
    if (pointersRef.current.size < 2) {
      pinchRef.current.active = false;
    }

    // drag bitti
    if (pointersRef.current.size === 0) {
      dragRef.current.active = false;
    }
  };

  const onDoubleClick = (e) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;

    const px = clamp(e.clientX - rect.left, 0, rect.width);
    const py = clamp(e.clientY - rect.top, 0, rect.height);

    // toggle: 1 <-> 2.2
    const next = scale > 1.05 ? 1 : 2.2;
    applyZoomAtPoint(next, px, py);
  };

  const scalePct = Math.round(scale * 100);

  return (
    <div className="md-zoom">
      <div className="md-zoom-toolbar">
        <div className="md-zoom-left">
          <span className="md-zoom-indicator">{scalePct}%</span>
        </div>

        <div className="md-zoom-right">
          <button
            type="button"
            className="btn-md btn-md-ghost"
            onClick={() => {
              const rect = stageRef.current?.getBoundingClientRect();
              if (!rect) return;
              applyZoomAtPoint(scale / 1.2, rect.width / 2, rect.height / 2);
            }}
            aria-label="Uzaklaştır"
          >
            −
          </button>

          <button
            type="button"
            className="btn-md btn-md-ghost"
            onClick={() => {
              const rect = stageRef.current?.getBoundingClientRect();
              if (!rect) return;
              applyZoomAtPoint(scale * 1.2, rect.width / 2, rect.height / 2);
            }}
            aria-label="Yakınlaştır"
          >
            +
          </button>

          <button type="button" className="btn-md btn-md-ghost" onClick={reset}>
            Sıfırla
          </button>
        </div>
      </div>

      <div
        ref={stageRef}
        className="md-zoom-stage"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
        onDoubleClick={onDoubleClick}
      >
        <img
          src={src}
          alt={alt || "Görsel"}
          className={`md-zoom-img ${className || ""}`.trim()}
          draggable={false}
          style={{
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            cursor: scale > 1.05 ? "grab" : "default",
          }}
        />
      </div>

      <div className="md-zoom-hint">
        İpucu: Fare tekeri ile zoom, sürükleyerek taşı, mobilde iki parmakla yakınlaştır.
      </div>
    </div>
  );
}

export default function BasicModal({
  open,
  title,
  children,
  onClose,
  actions,
  size = "lg",
  closeOnBackdrop = true,
  showClose = true,
  ariaLabel,
}) {
  const panelRef = useRef(null);
  const closeBtnRef = useRef(null);

  const titleId = useMemo(() => {
    return `md-modal-title-${Math.random().toString(36).slice(2, 9)}`;
  }, []);

  //   tek img yakala (Fragment/div/span içinden de)
  const zoomableImg = useMemo(() => extractSingleImg(children), [children]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  // ESC to close + focus initial
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    const t = window.setTimeout(() => {
      if (showClose && closeBtnRef.current) closeBtnRef.current.focus();
      else panelRef.current?.focus();
    }, 0);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(t);
    };
  }, [open, onClose, showClose]);

  // Focus trap (Tab/Shift+Tab)
  const handleKeyDown = (e) => {
    if (e.key !== "Tab") return;
    const root = panelRef.current;
    if (!root) return;

    const focusables = root.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (e.shiftKey) {
      if (active === first || active === root) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  if (!open) return null;

  const sizeClass =
    size === "sm"
      ? "md-modal-panel--sm"
      : size === "md"
      ? "md-modal-panel--md"
      : size === "xl"
      ? "md-modal-panel--xl"
      : "md-modal-panel--lg";

  const resolvedAriaLabel = ariaLabel || "Modal";

  return (
    <div
      className="md-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={!title ? resolvedAriaLabel : undefined}
    >
      <div
        className="md-modal-backdrop"
        onClick={() => {
          if (closeOnBackdrop) onClose?.();
        }}
      />

      <div
        className={`md-modal-panel ${sizeClass}`}
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="md-modal-head">
          <div className="md-modal-title" id={title ? titleId : undefined}>
            {title}
          </div>

          {showClose ? (
            <button
              type="button"
              className="btn-md btn-md-ghost md-modal-close"
              onClick={onClose}
              aria-label="Kapat"
              ref={closeBtnRef}
            >
              <XMarkIcon width={18} height={18} />
            </button>
          ) : null}
        </div>

        <div className="md-modal-body">
          {zoomableImg?.src ? (
            <ZoomableImage src={zoomableImg.src} alt={zoomableImg.alt} className={zoomableImg.className} />
          ) : (
            children
          )}
        </div>

        {actions ? <div className="md-modal-actions">{actions}</div> : null}
      </div>
    </div>
  );
}
